import React from "react";
import { Button, Form, Input } from "antd";
import { ForgotPasswordFormProps } from "../types/forgot-password.types";


function ForgotPasswordForm(props: ForgotPasswordFormProps) {

    const [form] = Form.useForm();

    async function onSubmit() {
        const formData = form.getFieldsValue();
        await props.onSubmit(formData);
    }

    const passwordSessionExpiry = `${String(Math.floor(props.passwordSessionTimer / 60)).padStart(2, "0")}:${String(props.passwordSessionTimer % 60).padStart(2, "0")}`;
    const isNearToExpiry = props.passwordSessionTimer <= 180;

    return (
        <Form

            form={form}
            layout="vertical"
            className="forget-password-form"
        >
            {/* Step 1 -> OTP Generation */}
            <Form.Item
                label="Email"
                name={"email"}
                validateTrigger="onChange"
                rules={[
                    {
                        required: true,
                        message: "Please enter the email id"
                    },
                    {
                        pattern: new RegExp(/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/),
                        message: "Please enter a valid email id",
                    },
                ]}
                hidden={props.activeStep !== 0}
            >
                <Input placeholder="Enter the email"/>
            </Form.Item>
            {props.activeStep === 0 && (
                <div className='forget-password-form__text-btn-wrapper'>
                    <p className={'forget-password-form__email-description'}>
                        We'll send a 4-digit OTP to this address.
                    </p>
                    <Form.Item shouldUpdate>
                        {() => {
                            const email = form.getFieldValue('email');
                            const hasError = form.getFieldError('email').length > 0;
                            const isValid = !!email && !hasError;

                            return (
                                <Button
                                    disabled={!isValid}
                                    className="forget-password-form__send-otp-btn"
                                    onClick={() => {
                                        onSubmit();
                                        form.setFieldValue('otp', null);
                                    }}
                                >
                                    Generate OTP
                                </Button>
                            );
                        }}
                    </Form.Item>
                </div>
            )}


            {/*Step 2: OTP confirmation*/}
            <Form.Item
                label="OTP"
                name="otp"
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                    {
                        required: true,
                        message: "Please enter the OTP"
                    },
                    {
                        pattern: new RegExp(/^\d{4}$/),
                        message: "OTP is not valid",
                    },
                ]}
                className={'forget-password-form__otp-input-wrapper'}
                hidden={props.activeStep !== 1}
            >
                <Input.OTP length={4}/>
                {/*<Input placeholder="Enter the OTP"/>*/}
            </Form.Item>

            {props.activeStep === 1 && (
                <>
                    <div className='forget-password-button'>
                        <Form.Item shouldUpdate>
                            {() => {
                                const isValid = form.getFieldValue('otp')?.length === 4;

                                return (
                                    <Button
                                        disabled={!isValid}
                                        type="primary"
                                        className={'forget-password-form__submit-otp-btn'}
                                        htmlType={'submit'}
                                        onClick={onSubmit}
                                    >
                                        Submit
                                    </Button>
                                );
                            }}
                        </Form.Item>
                    </div>
                    <div className='forget-password-form__text-btn-wrapper'>
                        <p className={'forget-password-form__resend-otp-description'}>
                            Didn’t receive the code? {props.otpTimer > 0 ? `Resend in ${props.otpTimer}` : 'Resend it'}.
                        </p>
                        <Button
                            disabled={props.otpTimer > 0}
                            className={`forget-password-form__resend-otp-btn ${props.otpTimer > 0 ? 'hide' : 'show'}`}
                            onClick={() => {
                                props.generateOtp({email: form.getFieldValue('email')})
                                form.setFieldValue('otp', null);
                            }}
                        >
                            Resend OTP
                        </Button>


                    </div>
                </>)}


            {/*step 3 : Reset Password*/}
            {props.activeStep === 2 && (
                <div className={`forget-password-form__pw-timer ${isNearToExpiry ? 'red' : 'yellow'}`}>
                    Password reset session expires in {passwordSessionExpiry}
                </div>
            )
            }
            <Form.Item
                name="password"
                label="New Password"
                hasFeedback
                rules={[
                    {required: true, message: "Please enter new password!"},
                    {
                        pattern: new RegExp(
                            /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
                        ),
                        message:
                            "Minimum eight characters, at least one uppercase letter, one lowercase letter, one special character and one number are required",
                    },
                ]}
                hidden={props.activeStep !== 2}
            >
                <Input.Password
                    autoComplete="new-password"
                    placeholder="enter new password"
                    className='forget-password-form__pw-input'
                />
            </Form.Item>
            <Form.Item
                className={'forget-password-form__confirm-pw-wrapper'}
                name="confirmPassword"
                label="Confirm Password"
                hasFeedback
                hidden={props.activeStep !== 2}
                rules={[
                    {
                        required: true,
                        message: "Please confirm your password!",
                    },
                    ({getFieldValue}) => ({
                        validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(
                                new Error(
                                    "The new password that you entered do not match!"
                                )
                            );
                        },
                    }),
                ]}
            >
                <Input.Password
                    autoComplete="new-password"
                    placeholder="enter confirm password"
                    className='forget-password-form__pw-input'
                />
            </Form.Item>

            <Form.Item shouldUpdate hidden={props.activeStep !== 2}>
                {() => {
                    const password = form.getFieldValue('password');
                    const confirmPassword = form.getFieldValue('confirmPassword');

                    const isValid =
                        password &&
                        confirmPassword &&
                        password === confirmPassword &&
                        form.getFieldError('password').length === 0 &&
                        form.getFieldError('confirmPassword').length === 0;

                    return (
                        <Button
                            disabled={!isValid}
                            type="primary"
                            className={'forget-password-form__reset-pw-btn'}
                            onClick={onSubmit}
                        >
                            Reset Password
                        </Button>
                    );
                }}
            </Form.Item>


        </Form>
    )
}

export default ForgotPasswordForm;
